import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react'
import { MapProvider, useMap } from '@widgets/Map'
import { getMapProviderComponent } from '@widgets/Map/lib'

import { MapConfig } from '@types'

import { MapAPI } from '../lib/types'
import {
  useLoadPointGroups,
  useMapMarkers,
  useMapObjectModels,
  useMapPointsSubscription,
} from '../model'

interface MapProps {
  config: MapConfig
  onError?: (error: Error) => void
  children?: ReactNode
}

/**
 * Внутренний компонент карты
 * Управляет жизненным циклом карты, загрузкой данных и отображением маркеров
 *
 * Поток данных карты:
 * 1) useMapObjectModels — загружает модели и PK для объектов pointGroups
 * 2) useLoadPointGroups — загружает initial данные точек
 * 3) useMapPointsSubscription — обновляет pointsData по WebSocket (CREATE/UPDATE/DELETE)
 * 4) useMapMarkers — синхронизирует маркеры на карте с pointsData
 */
const MapContent: FC<MapProps> = ({ config, onError, children }) => {
  const { setMapApi, mapApi, isReady } = useMap()

  // Мемоизируем компонент провайдера (зависит только от типа провайдера)
  const MapProviderComponent = useMemo(
    () => getMapProviderComponent(config.mapProvider),
    [config.mapProvider]
  )

  // Мемоизируем pointGroups для стабильности (предотвращает лишние перезагрузки)
  const pointGroups = useMemo(() => config.pointGroups || [], [config.pointGroups])

  // Обработчик готовности карты
  const handleMapReady = useCallback(
    (api: MapAPI) => {
      setMapApi(api)
    },
    [setMapApi, config.mapProvider]
  )

  // Cleanup: очистка API при размонтировании
  useEffect(() => {
    return () => {
      setMapApi(null)
    }
  }, [setMapApi])

  const {
    objectModelsMap,
    isReady: modelsReady,
    isLoading: modelsLoading,
  } = useMapObjectModels(pointGroups)

  // Загрузка данных точек (параллельно)
  const {
    data: pointsData,
    setData: setPointsData,
    isLoading: dataLoading,
  } = useLoadPointGroups(pointGroups)

  useMapMarkers(
    mapApi,
    isReady && modelsReady, // Отрисовываем маркеры только когда модели готовы
    pointGroups,
    pointsData,
    objectModelsMap
  )

  useMapPointsSubscription(
    isReady && modelsReady, // Подписка активна только когда карта и модели готовы
    pointGroups,
    setPointsData,
    objectModelsMap
  )

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapProviderComponent
        settings={config.mapSettings}
        onError={onError}
        onReady={handleMapReady}
      />
      {children}
    </div>
  )
}

/**
 * Главный компонент карты
 * Оборачивает в MapProvider для доступа к API
 */
export const Map: FC<MapProps> = props => {
  return (
    <MapProvider>
      <MapContent {...props} />
    </MapProvider>
  )
}
